import { LocalStorageService } from "./LocalStorageService";
import { toast } from "react-toastify";
import { t } from "i18next";
import PatientLocalStorage from "../../patient-app/PatientLocalStorage";

function showLoader() {
    if (document.getElementById('request-spinner'))
        document.getElementById('request-spinner').style.setProperty('display', 'block');
}

function removeLoader() {
    if (document.getElementById('request-spinner'))
        document.getElementById('request-spinner').style.setProperty('display', 'none');
}

function setLogout(func) {
    document.logout = func;
}

function getAuthorization() {
    const token = LocalStorageService.getToken();
    return `Bearer ${token}`;
}

window.queryList = []

const REQUEST_TIMEOUT_MS = 60_000; // Mobil zayıf ağ için 60sn üst sınır

async function safeReadJson(response) {
    try {
        return await response.json();
    } catch {
        return {};
    }
}

async function request({ endpoint, method, body, onValidationError, contentType, isResponseFile, showSuccess, hideLoader, browserDownload, hideError }) {
    const config = window.AppSettings

    if (!hideLoader)
        showLoader()

    // Offline shortcut: cihaz offline'sa fetch'i hiç deneme
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        removeLoader()
        if (!hideError)
            toast.error(t('İnternet bağlantısı yok'), { autoClose: 3000 })
        return { success: false, data: null, message: 'offline' }
    }

    // Timeout için AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
        const token = LocalStorageService.getToken();
        let headers = {};

        let url = new URL(`${(new URL(window.location.href).origin)}${endpoint}`);
        if (config.server.api)
            url = new URL(`${config.server.api}${endpoint}`);

        if (isResponseFile || browserDownload)
            url.searchParams.append('request_time', new Date().getTime().toString());

        if (browserDownload) {
            url.searchParams.append('access_token', token);
            const href = document.createElement('a');
            href.setAttribute('href', url)
            href.setAttribute('download', '')
            href.style.display = 'none'
            href.click();
            clearTimeout(timeoutId);
            removeLoader();
            return
        }

        if (contentType) {
            headers['Content-Type'] = contentType;
        }

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: JSON.stringify(body),
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.status === 401) {
            removeLoader()
            if (document.logout) document.logout()
            return { success: false }
        }

        let json = {};
        if (isResponseFile && response.status === 200) {
            const content = response.headers.get('Content-Disposition');
            const blobData = await response.blob();
            if (content) {
                const fileName = content.split(';')[1].split('=')[1].replaceAll('"', '');
                json = {
                    fileName,
                    success: true,
                    data: URL.createObjectURL(blobData),
                    blobData: blobData
                }
            }
            else {
                json = {
                    success: true,
                    data: URL.createObjectURL(blobData),
                    blobData: blobData
                }
            }
        }
        else {
            json = await safeReadJson(response);
        }

        removeLoader()

        if (response.status >= 200 && response.status < 300) {
            // 204 No Content
            if (response.status === 204) {
                json.message && toast.success(t(json.message));
                return { success: true, message: json.message }
            }
            if ((method === 'POST' || method === 'PUT') && showSuccess) {
                toast.success('İşlem Başarılı');
            }
            return { success: true, ...json }
        }

        // 4xx — validation / bad request / not found
        if (response.status >= 400 && response.status < 500) {
            if (typeof onValidationError === 'function') {
                onValidationError(json);
            }
            const message = json.Message || json.message;
            if (!hideError && message) toast.error(t(message), { autoClose: 4000 });
            return {
                success: false,
                status: response.status,
                message,
                errorData: json.ErrorData || json.errors,
            }
        }

        // 5xx — server error
        if (response.status >= 500) {
            const message = json.Message || json.message;
            if (!hideError) toast.error(t(message || 'Sunucu hatası'), { autoClose: 4000 });
            return {
                success: false,
                status: response.status,
                message,
                errorData: json.ErrorData,
            }
        }

        return { success: false, status: response.status }
    }
    catch (exp) {
        clearTimeout(timeoutId);
        removeLoader()

        const isAbort = exp && (exp.name === 'AbortError' || exp.code === 20);
        if (!hideError) {
            if (isAbort)
                toast.error(t('İstek zaman aşımına uğradı'), { autoClose: 3000 });
            else if (typeof navigator !== 'undefined' && navigator.onLine === false)
                toast.error(t('İnternet bağlantısı yok'), { autoClose: 3000 });
            else
                toast.error(t('İşlem Başarısız!'), { autoClose: 3000 });
        }

        return {
            success: false,
            data: null,
            message: isAbort ? 'timeout' : null,
        }
    }
}

function jsonRequest({ endpoint, body, method, onValidationError, isResponseFile, showSuccess, hideLoader, hideError, browserDownload }) {
    return request({
        endpoint,
        body,
        method,
        onValidationError,
        isResponseFile,
        contentType: 'application/json',
        showSuccess,
        hideLoader,
        browserDownload,
        hideError
    })
}

// Multipart dosya yükleme (jsonRequest JSON gövdesi gönderdiği için ayrı bir yol gerekir).
async function uploadRequest({ endpoint, file, fieldName = 'file' }) {
    const config = window.AppSettings;
    let url = `${new URL(window.location.href).origin}${endpoint}`;
    if (config && config.server && config.server.api)
        url = `${config.server.api}${endpoint}`;

    const formData = new FormData();
    formData.append(fieldName, file);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { Authorization: getAuthorization() },
            body: formData,
        });
        if (response.status === 401) {
            if (document.logout) document.logout();
            return { success: false };
        }
        const json = await safeReadJson(response);
        if (response.status >= 200 && response.status < 300)
            return { success: true, ...json };
        const message = json.Message || json.message;
        if (message) toast.error(t(message), { autoClose: 4000 });
        return { success: false, message };
    } catch {
        toast.error(t('Sunucu hatası'), { autoClose: 4000 });
        return { success: false };
    }
}

const Firm = {
    GetFirmSettings: async function getFirmSettings() {
        return jsonRequest({
            endpoint: `/api/Firm/GetFirmSettings`,
            method: 'GET'
        })
    },
    GetFirmList: async function getFirmSettings(all) {
        return jsonRequest({
            endpoint: `/api/Firm/GetFirmList/${all}`,
            method: 'GET'
        })
    },
}

const District = {
    GetDistrictList: (cityId) => {
        return jsonRequest({
            endpoint: `/api/District/GetDistrictList/${cityId}`,
            method: 'GET'
        })
    }
}

const City = {
    GetCityList: () => {
        return jsonRequest({
            endpoint: `/api/City/GetCityList`,
            method: 'GET'
        })
    }
}

const AppointmentTeletip = {
    SendRequestMessages: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/SendRequestMessages/${appointmentId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetHl7Messages: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/GetHl7Messages/${appointmentId}`,
            method: 'GET'
        })
    },
    GetHl7Message: (messageId) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/GetHl7Message/${messageId}`,
            method: 'GET'
        })
    },
    SendEnabizBatch: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/SendEnabizBatch/${appointmentId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetTeletipOrderStatus: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/GetTeletipOrderStatus/${appointmentId}`,
            method: 'GET'
        })
    },
    SendReportMessage: (messageId) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/SendReportMessage/${messageId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    RemoveKos: (appointmentOperationId, studyInstanceUid) => {
        return jsonRequest({
            endpoint: `/api/AppointmentTeletip/RemoveKos/${appointmentOperationId}`,
            method: 'POST',
            body: { studyInstanceUid },
            showSuccess: true
        })
    }
}

const EnabizOperation = {
    GetEnabizOperationDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/EnabizOperation/GetEnabizOperationDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    SendRisJob: (appointmentOperationId) => {
        return jsonRequest({
            endpoint: `/api/EnabizOperation/SendRisJob/${appointmentOperationId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    SendRequestHl7: (appointmentOperationId) => {
        return jsonRequest({
            endpoint: `/api/EnabizOperation/SendRequestHl7/${appointmentOperationId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    SendReportHl7: (appointmentOperationId) => {
        return jsonRequest({
            endpoint: `/api/EnabizOperation/SendReportHl7/${appointmentOperationId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    SendRisHl7: (appointmentOperationId) => {
        return jsonRequest({
            endpoint: `/api/EnabizOperation/SendRisHl7/${appointmentOperationId}`,
            method: 'POST',
            showSuccess: true
        })
    },
}

const Barcode = {
    GetBarcode: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Barcode/GetBarcode/${appointmentId}`,
            method: 'GET',
            showSuccess: false,
            isResponseFile: true
        })
    },
}

const Appointment = {
    GetOnamFormWord: (appointmentId, formId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetOnamFormWord/${appointmentId}/${formId}?${new Date().getTime()}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    GetOnamFormPdf: (appointmentId, formId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetOnamFormPdf/${appointmentId}/${formId}?${new Date().getTime()}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    EditAppointmentState: (appointmentId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/EditAppointmentState/${appointmentId}`,
            method: 'PUT',
            body: data
        })
    },
    GetAppointmentDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetAppointmentDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetAppointmentDataGridSummary: (filter) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetAppointmentDataGridSummary`,
            method: 'POST',
            body: filter
        })
    },
    GetTotalPatientBalance: (patientId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetTotalPatientBalance?patientId=${patientId}`,
            method: 'POST',
            body: data
        })
    },
    GetLastAppointmentList: () => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetLastAppointmentList`,
            method: 'GET'
        })
    },
    GetPatientQueue: () => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetPatientQueue`,
            method: 'GET'
        })
    },
    GetLastAppointmentPatients: () => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetLastAppointmentPatients`,
            method: 'GET'
        })
    },
    CheckIn: (appointmentId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/CheckIn/${appointmentId}`,
            method: 'POST',
            body: data
        })
    },
    EditAppointmentDate: (appointmentId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/EditAppointmentDate/${appointmentId}`,
            method: 'POST',
            body: data
        })
    },
    GetAppointmentList: (filter) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetAppointmentList`,
            method: 'POST',
            body: filter
        })
    },
    CreateAppointment: (data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/CreateAppointment`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    EditAppointment: (appointmentId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/EditAppointment/${appointmentId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetAppointment: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetAppointment/${appointmentId}`,
            method: 'GET',
        })
    },
    GetOperationsByAppointmentIds: (appointmentIds) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetOperationsByAppointmentIds`,
            method: 'POST',
            body: appointmentIds
        })
    },
    DeleteAppointment: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/DeleteAppointment/${appointmentId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    SendReminderNotification: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/SendReminderNotification/${appointmentId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    RestoreAppointment: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/RestoreAppointment/${appointmentId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetExcelAppointments: (filter) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetExcelAppointments`,
            method: 'POST',
            body: filter,
            isResponseFile: true
        })
    },
    UploadFile: (appointmentId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/UploadFile/${appointmentId}`,
            method: 'POST',
            body: data,
            showSuccess: false
        })
    },
    AddFileToAppointment: (fileId, appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/AddFileToAppointment/${fileId}/${appointmentId}`,
            method: 'POST',
            showSuccess: false
        })
    },
    GetFiles: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetFiles/${appointmentId}`,
            method: 'GET',
            showSuccess: false
        })
    },
    GetLastFiles: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetLastFiles/${appointmentId}`,
            method: 'GET',
            showSuccess: false
        })
    },
    GetAppointmentTypes: () => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetAppointmentTypes`,
            method: 'GET',
            showSuccess: false
        })
    },
    DeleteFile: (appointmentId, fileId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/DeleteFile/${appointmentId}/${fileId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    UpdateFileInformation: (appointmentId, fileId, data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/UpdateFileInformation/${appointmentId}/${fileId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    DownloadFile: (appointmentId, fileId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/DownloadFile/${appointmentId}/${fileId}?${new Date().getTime()}`,
            method: 'GET',
            showSuccess: false,
            isResponseFile: true
        })
    },
    GetFileUrl: (appointmentId, fileId) => {
        return `${window.AppSettings.server.api}/api/Appointment/DownloadFile/${appointmentId}/${fileId}?${new Date().getTime()}&access_token=${LocalStorageService.getToken()}`
    },
    UpdatePatient: (appointmentId, patientId) => {
        return jsonRequest({
            endpoint: `/api/Appointment/UpdatePatient/${appointmentId}/${patientId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetInstitutionStatistics: () => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetInstitutionStatistics`,
            method: 'GET'
        })
    },
    UpdateInvoiceNo: (appointmentId, invoiceNo) => {
        return jsonRequest({
            endpoint: `/api/Appointment/UpdateInvoiceNo/${appointmentId}/${invoiceNo}`,
            method: 'POST',
            showSuccess: true
        })
    },
    BlockSlot: (data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/BlockSlot`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    UnblockSlot: (id) => {
        return jsonRequest({
            endpoint: `/api/Appointment/UnblockSlot/${id}`,
            method: 'POST',
            showSuccess: true
        })
    },
    UpdateBlockedSlot: (data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/UpdateBlockedSlot`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetBlockedSlots: (data) => {
        return jsonRequest({
            endpoint: `/api/Appointment/GetBlockedSlots`,
            method: 'POST',
            body: data
        })
    },
}

const Operation = {
    GetOperationList: (unitId) => {
        return jsonRequest({
            endpoint: `/api/Operation/GetOperationList/${unitId}`,
            method: `GET`,
        })
    },
    GetAllOperationList: () => {
        return jsonRequest({
            endpoint: `/api/Operation/GetAllOperationList`,
            method: `GET`,
        })
    },
    GetOperation: (operationId) => {
        return jsonRequest({
            endpoint: `/api/Operation/GetOperation/${operationId}`,
            method: `GET`,
        })
    },
    CreateOperation: (data) => {
        return jsonRequest({
            endpoint: `/api/Operation/CreateOperation`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditOperation: (operationId, data) => {
        return jsonRequest({
            endpoint: `/api/Operation/EditOperation/${operationId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteOperation: (operationId) => {
        return jsonRequest({
            endpoint: `/api/Operation/DeleteOperation/${operationId}`,
            method: `DELETE`,
            showSuccess: true
        })
    },
    GetInstitutionPriceList: (institutionId, operationIdList) => {
        return jsonRequest({
            endpoint: `/api/Operation/GetInstitutionPriceList/${institutionId}`,
            method: `POST`,
            body: operationIdList
        })
    },
    GetAppointmentTypePriceList: (typeId, operationIdList) => {
        return jsonRequest({
            endpoint: `/api/Operation/GetAppointmentTypePriceList/${typeId}`,
            method: `POST`,
            body: operationIdList
        })
    },
    GetExcelPriceList: (type) => {
        return jsonRequest({
            endpoint: `/api/Operation/GetExcelPriceList/${type}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    UpdatePriceFromExcel: (data) => {
        return jsonRequest({
            endpoint: `/api/Operation/UpdatePriceFromExcel`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    UpdateOperationPrice: (operationId, institutionId, price) => {
        return jsonRequest({
            endpoint: `/api/Operation/UpdateOperationPrice/${operationId}/${institutionId}/${price}`,
            method: `POST`,
            showSuccess: true
        })
    },
    GetOperationSubItemsExcel: (operationId) => {
        return jsonRequest({
            endpoint: `/api/Operation/GetOperationSubItemsExcel/${operationId}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    UpdateOperationSubItemsExcel: (data) => {
        return jsonRequest({
            endpoint: `/api/Operation/UpdateOperationSubItemsExcel`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
}

const AppLog = {
    GetAppLogDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/AppLog/GetAppLogDataGrid`,
            method: 'POST',
            body: gridInput,
        })
    },
    GetAppLogsByAppointmentId: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/AppLog/GetAppLogsByAppointmentId/${appointmentId}`,
            method: 'GET',
        })
    },
    GetAppLogTypes: () => {
        return jsonRequest({
            endpoint: `/api/AppLog/GetAppLogTypes`,
            method: 'GET',
        })
    }
}

const FirmPosDevice = {
    GetFirmPosDevices: (filter) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/GetFirmPosDevices`,
            method: 'POST',
            body: filter || {},
            showSuccess: false
        })
    },
    GetFirmPosDevice: (id) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/GetFirmPosDevice/${id}`,
            method: 'GET',
            showSuccess: false
        })
    },
    CreateFirmPosDevice: (data) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/CreateFirmPosDevice`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    EditFirmPosDevice: (id, data) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/EditFirmPosDevice/${id}`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    DeleteFirmPosDevice: (id) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/DeleteFirmPosDevice/${id}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    GetPairingRequest: (id) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/GetPairingRequest/${id}`,
            method: 'GET',
            showSuccess: false
        })
    },
    SavePairing: (id, input) => {
        return jsonRequest({
            endpoint: `/api/FirmPosDevice/SavePairing/${id}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
}

const SmsLog = {
    GetSmsLogDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/SmsLog/GetSmsLogDataGrid`,
            method: 'POST',
            body: gridInput,
        })
    },
    GetMailLogDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/SmsLog/GetMailLogDataGrid`,
            method: 'POST',
            body: gridInput,
        })
    },
    SendTestSms: (input) => {
        return jsonRequest({
            endpoint: `/api/SmsLog/SendTestSms`,
            method: 'POST',
            body: input,
        })
    }
}

const Patient = {
    GetToken: (patientId) => {
        return jsonRequest({
            endpoint: `/api/Patient/GetPatientToken/${patientId}`,
            method: `GET`,
        })
    },
    GetPatientDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/Patient/GetPatientDataGrid`,
            method: 'POST',
            body: gridInput,
        })
    },
    SearchPatient: (filter) => {
        return jsonRequest({
            endpoint: `/api/Patient/SearchPatient`,
            method: 'POST',
            body: filter,
            hideLoader: true
        })
    },
    GetPatientByCustomId: (customId, hideLoader) => {
        return jsonRequest({
            endpoint: `/api/Patient/GetPatientByCustomId/${customId}`,
            method: `GET`,
            hideLoader: hideLoader,
            showSuccess: false,
            hideError: true
        })
    },
    GetPatient: (patientId) => {
        return jsonRequest({
            endpoint: `/api/Patient/GetPatient/${patientId}`,
            method: `GET`,
        })
    },
    CreatePatient: (data, showSuccess) => {
        return jsonRequest({
            endpoint: `/api/Patient/CreatePatient`,
            method: `POST`,
            body: data,
            showSuccess
        })
    },
    EditPatient: (patientId, data, showSuccess, hideError) => {
        return jsonRequest({
            endpoint: `/api/Patient/EditPatient/${patientId}`,
            method: `POST`,
            body: data,
            showSuccess,
            hideError
        })
    },
    IsConsentApproved: (patientId, documentType) => {
        return jsonRequest({
            endpoint: `/api/Patient/IsConsentApproved/${patientId}/${documentType}`,
            method: `GET`
        })
    },
    SendConsentCode: (patientId, documentType, channel) => {
        return jsonRequest({
            endpoint: `/api/Patient/SendConsentCode/${patientId}/${documentType}/${channel}`,
            method: `POST`,
            showSuccess: true
        })
    },
    ApproveConsent: (patientId, documentType, data) => {
        return jsonRequest({
            endpoint: `/api/Patient/ApproveConsent/${patientId}/${documentType}`,
            method: `POST`,
            body: data
        })
    }
}

const Unit = {
    UploadDefaultForm: (unitId, data) => {
        return jsonRequest({
            endpoint: `/api/Unit/UploadDefaultForm/${unitId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteUnitForm: (unitId, formId) => {
        return jsonRequest({
            endpoint: `/api/Unit/DeleteUnitForm/${unitId}/${formId}`,
            method: `DELETE`,
            showSuccess: true
        })
    },
    GetDefaultForm: (unitId, formId) => {
        return jsonRequest({
            endpoint: `/api/Unit/GetDefaultForm/${unitId}/${formId}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    GetUnitList: () => {
        return jsonRequest({
            endpoint: `/api/Unit/GetUnitList`,
            method: `GET`,
        })
    },
    GetUnit: (unitId) => {
        return jsonRequest({
            endpoint: `/api/Unit/GetUnit/${unitId}`,
            method: `GET`,
        })
    },
    CreateUnit: (data) => {
        return jsonRequest({
            endpoint: `/api/Unit/CreateUnit`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditUnit: (unitId, data) => {
        return jsonRequest({
            endpoint: `/api/Unit/EditUnit/${unitId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteUnit: (unitId) => {
        return jsonRequest({
            endpoint: `/api/Unit/DeleteUnit/${unitId}`,
            method: `DELETE`,
            showSuccess: true
        })
    }
}

const Institution = {
    GetInstitutionList: (enabled) => {
        return jsonRequest({
            endpoint: `/api/Institution/GetInstitutionList/${enabled}`,
            method: `GET`,
        })
    },
    GetInstitutionListByFirm: (firmId) => {
        return jsonRequest({
            endpoint: `/api/Institution/GetInstitutionListByFirm/${firmId}`,
            method: `GET`,
        })
    },
    GetInstitution: (institutionId) => {
        return jsonRequest({
            endpoint: `/api/Institution/GetInstitution/${institutionId}`,
            method: `GET`,
        })
    },
    GetInstitutionTypes: () => {
        return jsonRequest({
            endpoint: `/api/Institution/GetInstitutionTypes`,
            method: `GET`,
        })
    },
    GetInstitutionFirms: (institutionId) => {
        return jsonRequest({
            endpoint: `/api/Institution/GetInstitutionFirms/${institutionId}`,
            method: `GET`,
        })
    },
    AddInstitutionFirm: (institutionId, data) => {
        return jsonRequest({
            endpoint: `/api/Institution/AddInstitutionFirm/${institutionId}`,
            method: `POST`,
            body: data
        })
    },
    EditInstitutionFirm: (institutionId, firmId, data) => {
        return jsonRequest({
            endpoint: `/api/Institution/EditInstitutionFirm/${institutionId}/${firmId}`,
            method: `POST`,
            body: data
        })
    },
    DeleteInstitutionFirm: (institutionId, firmId) => {
        return jsonRequest({
            endpoint: `/api/Institution/DeleteInstitutionFirm/${institutionId}/${firmId}`,
            method: `DELETE`,
        })
    },
    CreateInstitution: (data) => {
        return jsonRequest({
            endpoint: `/api/Institution/CreateInstitution`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditInstitution: (institutionId, data) => {
        return jsonRequest({
            endpoint: `/api/Institution/EditInstitution/${institutionId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteInstitution: (institutionId) => {
        return jsonRequest({
            endpoint: `/api/Institution/DeleteInstitution/${institutionId}`,
            method: `DELETE`,
            showSuccess: true
        })
    }
}

const CashRegister = {
    GetCashRegisters: (all) => {
        return jsonRequest({
            endpoint: `/api/CashRegister/GetCashRegisters/${all}`,
            method: `GET`,
        })
    },
    GetCashRegister: (id) => {
        return jsonRequest({
            endpoint: `/api/CashRegister/GetCashRegister/${id}`,
            method: `GET`,
        })
    },
    CreateCashRegister: (data) => {
        return jsonRequest({
            endpoint: `/api/CashRegister/CreateCashRegister`,
            method: `POST`,
            body: data
        })
    },
    EditCashRegister: (id, data) => {
        return jsonRequest({
            endpoint: `/api/CashRegister/EditCashRegister/${id}`,
            method: `POST`,
            body: data
        })
    },
}

const PatientBillingAddress = {
    GetBillingAddressList: (filter) => {
        return jsonRequest({
            endpoint: `/api/PatientBillingAddress/GetBillingAddressList`,
            method: `POST`,
            body: filter
        })
    },
    GetBillingAddress: (billingAddressId) => {
        return jsonRequest({
            endpoint: `/api/PatientBillingAddress/GetBillingAddress/${billingAddressId}`,
            method: `GET`
        })
    },
    CreateBillingAddress: (data) => {
        return jsonRequest({
            endpoint: `/api/PatientBillingAddress/CreateBillingAddress`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditBillingAddress: (billingAddressId, data) => {
        return jsonRequest({
            endpoint: `/api/PatientBillingAddress/EditBillingAddress/${billingAddressId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteBillingAddress: (billingAddressId) => {
        return jsonRequest({
            endpoint: `/api/PatientBillingAddress/DeleteBillingAddress/${billingAddressId}`,
            method: `DELETE`,
            showSuccess: true
        })
    }
}

const Reminder = {
    GetReminderList: (filter) => {
        return jsonRequest({
            endpoint: `/api/Reminder/GetReminderList`,
            method: `POST`,
            body: filter
        })
    },
    GetReminder: (reminderId) => {
        return jsonRequest({
            endpoint: `/api/Reminder/GetReminder/${reminderId}`,
            method: `GET`
        })
    },
    CreateReminder: (data) => {
        return jsonRequest({
            endpoint: `/api/Reminder/CreateReminder`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditReminder: (reminderId, data) => {
        return jsonRequest({
            endpoint: `/api/Reminder/EditReminder/${reminderId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteReminder: (reminderId) => {
        return jsonRequest({
            endpoint: `/api/Reminder/DeleteReminder/${reminderId}`,
            method: `DELETE`,
            showSuccess: true
        })
    }
}

const Doctor = {
    GetDoctorList: (filter) => {
        return jsonRequest({
            endpoint: `/api/Doctor/GetDoctorList`,
            method: `POST`,
            body: filter
        })
    },
    GetDoctor: (doctorId) => {
        return jsonRequest({
            endpoint: `/api/Doctor/GetDoctor/${doctorId}`,
            method: `GET`
        })
    },
    CreateDoctor: (data) => {
        return jsonRequest({
            endpoint: `/api/Doctor/CreateDoctor`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditDoctor: (doctorId, data) => {
        return jsonRequest({
            endpoint: `/api/Doctor/EditDoctor/${doctorId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    DeleteDoctor: (doctorId) => {
        return jsonRequest({
            endpoint: `/api/Doctor/DeleteDoctor/${doctorId}`,
            method: `DELETE`,
            showSuccess: true
        })
    },
}

const User = {
    Information: () => {
        return jsonRequest({
            endpoint: `/api/User/Information`,
            method: `GET`
        })
    },
    GetUserDashboards: () => {
        return jsonRequest({
            endpoint: `/api/User/GetUserDashboards`,
            method: `GET`
        })
    },
    GetDashboardLink: (id) => {
        return jsonRequest({
            endpoint: `/api/User/GetDashboardLink/${id}`,
            method: `GET`
        })
    },
    GetUser: (userId) => {
        return jsonRequest({
            endpoint: `/api/User/Get/${userId}`,
            method: `GET`
        })
    },
    GetUserList: (filter) => {
        return jsonRequest({
            endpoint: `/api/User/GetUserList`,
            method: `POST`,
            body: filter
        })
    },
    GetDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/User/GetDataGrid`,
            method: `POST`,
            body: gridInput
        })
    },
    CreateUser: (data) => {
        return jsonRequest({
            endpoint: `/api/User/CreateUser`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditUser: (userId, data) => {
        return jsonRequest({
            endpoint: `/api/User/EditUser/${userId}`,
            method: `PUT`,
            body: data,
            showSuccess: true
        })
    },
    // User Settings
    GetUserSettings: () => {
        return jsonRequest({
            endpoint: `/api/User/GetUserSettings`,
            method: `GET`,
            hideLoader: true
        })
    },
    MarkIntroShown: (introKey) => {
        return jsonRequest({
            endpoint: `/api/User/MarkIntroShown`,
            method: `POST`,
            body: { introKey },
            hideLoader: true
        })
    },
    UpdateUserSettings: (settings) => {
        return jsonRequest({
            endpoint: `/api/User/UpdateUserSettings`,
            method: `POST`,
            body: settings,
            hideLoader: true
        })
    },
}

const PaymentMethod = {
    GetPaymentMethodList: () => {
        return jsonRequest({
            endpoint: `/api/PaymentMethod/GetPaymentMethodList`,
            method: 'GET',
        })
    }
}

const Payment = {
    GetPaymentList: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Payment/GetPaymentList/${appointmentId}`,
            method: 'GET',
        })
    },
    GetPaymentsByAppointmentIds: (appointmentIds) => {
        return jsonRequest({
            endpoint: `/api/Payment/GetPaymentsByAppointmentIds`,
            method: 'POST',
            body: appointmentIds
        })
    },
    AddPayments: (appointmentIds, data) => {
        const queryString = appointmentIds.map(value => `appointmentIds=${value}`).join('&');
        return jsonRequest({
            endpoint: `/api/Payment/AddPayments?${queryString}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetPosDevices: (appointmentId) => {
        return jsonRequest({
            endpoint: `/api/Payment/GetPosDevices/${appointmentId}`,
            method: 'GET',
            showSuccess: false
        })
    },
    PavoGetSaleData: (data) => {
        return jsonRequest({
            endpoint: `/api/Payment/PavoGetSaleData`,
            method: 'POST',
            body: data,
            showSuccess: false
        })
    },
    PavoCompleteSale: (data) => {
        return jsonRequest({
            endpoint: `/api/Payment/PavoCompleteSale`,
            method: 'POST',
            body: data,
            showSuccess: false
        })
    },
    PavoCancelBody: (paymentId, orderId) => {
        return jsonRequest({
            endpoint: `/api/Payment/PavoCancelBody/${paymentId}/${orderId}`,
            method: 'POST',
            showSuccess: false
        })
    },
    PavoCancel: (paymentId) => {
        return jsonRequest({
            endpoint: `/api/Payment/PavoCancel/${paymentId}`,
            method: 'DELETE',
            showSuccess: false
        })
    },
    LogPosTransaction: (data) => {
        return jsonRequest({
            endpoint: `/api/Payment/LogPosTransaction`,
            method: 'POST',
            body: data,
            showSuccess: false
        })
    },
    EditPayment: (appointmentId, paymentId, data) => {
        return jsonRequest({
            endpoint: `/api/Payment/EditPayment/${appointmentId}/${paymentId}`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    DeletePayment: (appointmentId, paymentId) => {
        return jsonRequest({
            endpoint: `/api/Payment/DeletePayment/${appointmentId}/${paymentId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
}

const State = {
    GetStateListByCheckin: (checkInRequired) => {
        return jsonRequest({
            endpoint: `/api/State/GetStateListByCheckin/${checkInRequired}`,
            method: 'GET',
            hideLoader: true
        })
    },
    GetStateList: () => {
        return jsonRequest({
            endpoint: `/api/State/GetStateList`,
            method: 'GET',
        })
    },
    GetStateGroupList: () => {
        return jsonRequest({
            endpoint: `/api/State/GetStateGroupList`,
            method: 'GET',
        })
    },
    GetStateGroupDescList: () => {
        return jsonRequest({
            endpoint: `/api/State/GetStateGroupDescList`,
            method: 'GET',
        })
    },
}

const Expense = {
    GetExpenseDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/Expense/GetExpenseDataGrid`,
            method: 'POST',
            body: gridInput,
        })
    },
    GetExpenseGridSummary: (filter) => {
        return jsonRequest({
            endpoint: `/api/Expense/GetExpenseGridSummary`,
            method: 'POST',
            body: filter
        })
    },
    GetExpense: (expenseId) => {
        return jsonRequest({
            endpoint: `/api/Expense/GetExpense/${expenseId}`,
            method: 'GET',
        })
    },
    AddExpense: (data) => {
        return jsonRequest({
            endpoint: `/api/Expense/AddExpense`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    EditExpense: (expenseId, data) => {
        return jsonRequest({
            endpoint: `/api/Expense/EditExpense/${expenseId}`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    DeleteExpense: (expenseId) => {
        return jsonRequest({
            endpoint: `/api/Expense/DeleteExpense/${expenseId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
}

const CashView = {
    GetDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/CashView/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetSummary: (filter) => {
        return jsonRequest({
            endpoint: `/api/CashView/GetSummary`,
            method: 'POST',
            body: filter
        })
    },
    ToExcel: (filter) => {
        return jsonRequest({
            endpoint: `/api/CashView/ToExcel`,
            method: 'POST',
            body: filter,
            isResponseFile: true
        })
    },
    ToExcelParasut: (filter) => {
        return jsonRequest({
            endpoint: `/api/CashView/ToExcelParasut`,
            method: 'POST',
            body: filter,
            isResponseFile: true
        })
    },
}

const Dashboard = {
    GetAppointmentStatisticByStateGroup: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetAppointmentStatisticByStateGroup`,
            method: 'POST',
            body: filter
        })
    },
    GetAppointmentStatisticByState: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetAppointmentStatisticByState`,
            method: 'POST',
            body: filter
        })
    },
    GetAppointmentStatisticByUnit: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetAppointmentStatisticByUnit`,
            method: 'POST',
            body: filter
        })
    },
    GetCashRegisterByDay: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetCashRegisterByDay`,
            method: 'POST',
            body: filter
        })
    },
    GetCashRegisterByWeek: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetCashRegisterByWeek`,
            method: 'POST',
            body: filter
        })
    },
    GetCashRegisterByMonth: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetCashRegisterByMonth`,
            method: 'POST',
            body: filter
        })
    },
    GetCashRegisterUnitsByDay: (filter) => {
        return jsonRequest({
            endpoint: `/api/Dashboard/GetCashRegisterUnitsByDay`,
            method: 'POST',
            body: filter
        })
    }
}

const Role = {
    GetRoleList: () => {
        return jsonRequest({
            endpoint: `/api/Role/GetRoleList`,
            method: 'GET'
        })
    },
    GetRole: (roleId) => {
        return jsonRequest({
            endpoint: `/api/Role/GetRole/${roleId}`,
            method: 'GET'
        })
    },
    CreateRole: (data) => {
        return jsonRequest({
            endpoint: `/api/Role/CreateRole`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    EditRole: (roleId, data) => {
        return jsonRequest({
            endpoint: `/api/Role/EditRole/${roleId}`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    GetPermissionGroups: () => {
        return jsonRequest({
            endpoint: `/api/Role/GetPermissionGroups`,
            method: 'GET'
        })
    },
}

const Auth = {
    Login: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/Login`,
            method: 'POST',
            body: {
                ...data,
            },
            showSuccess: true
        })
    },
    SmsVerify: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/SmsVerify`,
            method: 'POST',
            body: {
                ...data,
            },
            showSuccess: true
        })
    },
    SendCode: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/SendCode`,
            method: 'POST',
            body: {
                ...data
            },
            showSuccess: true
        })
    },
}

const Notification = {
    GetNotificationList: async function (page) {
        return jsonRequest({
            endpoint: `/api/Notification/GetNotificationList/${page}`,
            method: 'GET',
            hideLoader: true,
            hideError: true
        })
    },
    SetRead: async function (id) {
        return jsonRequest({
            endpoint: `/api/Notification/SetRead/${id}`,
            method: 'PUT',
            hideLoader: true,
            hideError: true
        })
    },
    RemoveItem: async function (id) {
        return jsonRequest({
            endpoint: `/api/Notification/RemoveItem/${id}`,
            method: 'DELETE',
            hideLoader: true,
            hideError: true
        })
    },
    Clear: async function () {
        return jsonRequest({
            endpoint: `/api/Notification/Clear`,
            method: 'DELETE',
            hideLoader: true,
            hideError: true
        })
    },
}

const Entitlement = {
    UpdateEntitlement: (appointmentId, data) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/UpdateEntitlement/${appointmentId}`,
            method: 'POST',
            body: data
        })
    },
    GetExcelEntitlements: (filter) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/GetExcelEntitlements`,
            method: 'POST',
            body: filter,
            isResponseFile: true
        })
    },
    GetEntitlementConfirmUsers: () => {
        return jsonRequest({
            endpoint: `/api/Entitlement/GetEntitlementConfirmUsers`,
            method: 'GET',
        })
    },
    SendConfirmation: (data) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/SendConfirmation`,
            method: 'POST',
            body: data,
        })
    },
    CancelConfirmation: (data) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/CancelConfirmation`,
            method: 'POST',
            body: data,
        })
    },
    ConfirmEntitlements: (data) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/ConfirmEntitlements`,
            method: 'POST',
            body: data,
        })
    },
    UpdateAppointmentResponsible: (appointmentId, userId) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/UpdateAppointmentResponsible/${appointmentId}/${userId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetEntitlementDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/GetEntitlementDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetEntitlementGridSummary: (filter) => {
        return jsonRequest({
            endpoint: `/api/Entitlement/GetEntitlementGridSummary`,
            method: 'POST',
            body: filter
        })
    },
}

const Accounting = {
    UpdateProvisionNo: (appointmentId, provisionNo) => {
        return jsonRequest({
            endpoint: `/api/Accounting/UpdateProvisionNo/${appointmentId}/${provisionNo}`,
            method: 'POST',
            showSuccess: true
        })
    },
    UpdatePaymentStatus: (appointmentId, paymentStatus) => {
        return jsonRequest({
            endpoint: `/api/Accounting/UpdatePaymentStatus/${appointmentId}/${paymentStatus}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetAccountingDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/Accounting/GetAccountingDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetExcelAppointmentAccounting: (filter) => {
        return jsonRequest({
            endpoint: `/api/Accounting/GetExcelAppointmentAccounting`,
            method: 'POST',
            body: filter,
            isResponseFile: true
        })
    }
}

const PatientApp = {
    GetReports: () => {
        return jsonRequest({
            endpoint: `/api/PatientApp/GetReports`,
            method: 'GET',
        })
    },
    GetStudies: () => {
        return jsonRequest({
            endpoint: `/api/PatientApp/GetStudies`,
            method: 'GET',
        })
    },
    // Mints a study-scoped access key on RIS and returns ready-to-use RIS URLs
    // ({ accessKey, expiry, viewerUrl, zipUrl, reports:[{ reportId, requestId, title, pdfUrl }] }).
    // tokenType: 1 = 12h, 2 = 24h, 3 = 72h, otherwise 24h.
    GetStudyAccess: (studyId, tokenType = 2, purpose = null) => {
        return jsonRequest({
            endpoint: `/api/PatientApp/GetStudyAccess/${studyId}/${tokenType}${purpose ? `?purpose=${purpose}` : ''}`,
            method: 'GET',
            showSuccess: false
        })
    },
    GetAccessToken: (tokenType) => {
        return jsonRequest({
            endpoint: `/api/PatientApp/GetAccessToken/${tokenType}`,
            method: 'GET',
            showSuccess: false
        })
    },
    // Direct, HIS-proxied DICOM zip download (DICOM + embedded viewer). Returns a ready-to-open URL carrying the
    // patient JWT as a query param so the browser can stream it straight from HIS via a plain anchor download.
    GetStudyZipUrl: (studyId) => {
        return `${window.AppSettings.server.api}/api/PatientApp/DownloadStudyZip/${studyId}?${new Date().getTime()}&access_token=${LocalStorageService.getToken()}`
    },
    GetPatient: () => {
        return jsonRequest({
            endpoint: `/api/PatientApp/GetPatient`,
            method: 'GET',
        })
    },
    DownloadFile: (fileId) => {
        return jsonRequest({
            endpoint: `/api/PatientApp/DownloadFile/${fileId}?${new Date().getTime()}`,
            method: 'GET',
            showSuccess: false,
            isResponseFile: true
        })
    },
    // Stores a long URL behind a GUID so it can be shared as a short {api}/api/PatientApp/GetUrl/{id} link
    // (used for the /ProfileShare?access_token=<jwt> profile-share link).
    CreateUrl: (url) => {
        return jsonRequest({
            endpoint: `/api/PatientApp/CreateUrl`,
            method: 'POST',
            body: url,
            showSuccess: false
        })
    },
    // Login sonrası ticari ileti onay modalı: içerik + karar kaydı.
    GetTicariConsentPrompt: () => {
        return jsonRequest({
            endpoint: `/api/PatientApp/GetTicariConsentPrompt`,
            method: 'GET',
            hideLoader: true,
            showSuccess: false,
        })
    },
    SetTicariConsent: (approved) => {
        return jsonRequest({
            endpoint: `/api/PatientApp/SetTicariConsent`,
            method: 'POST',
            body: {approved},
            showSuccess: false,
        })
    },
}

const AiChat = {
    // Session methods
    CreateSession: () => {
        return jsonRequest({
            endpoint: `/api/AiChat/CreateSession`,
            method: 'POST',
            hideLoader: true,
        })
    },
    GetSessions: () => {
        return jsonRequest({
            endpoint: `/api/AiChat/GetSessions`,
            method: 'GET',
            hideLoader: true,
        })
    },
    DeleteSession: (sessionId) => {
        return jsonRequest({
            endpoint: `/api/AiChat/DeleteSession/${sessionId}`,
            method: 'DELETE',
            hideLoader: true,
        })
    },
    GetSessionMessages: (sessionId) => {
        return jsonRequest({
            endpoint: `/api/AiChat/GetSessionMessages/${sessionId}`,
            method: 'GET',
            hideLoader: true,
        })
    },
    // Chat methods
    Ask: (input) => {
        return jsonRequest({
            endpoint: `/api/AiChat/Ask`,
            method: 'POST',
            body: input,
            hideLoader: true,
        })
    },
    RerunChat: (chatId) => {
        return jsonRequest({
            endpoint: `/api/AiChat/RerunChat/${chatId}`,
            method: 'POST'
        })
    },
    GetChat: (chatId) => {
        return jsonRequest({
            endpoint: `/api/AiChat/GetChat/${chatId}`,
            method: 'GET'
        })
    },
    GetChatHistory: (filter) => {
        return jsonRequest({
            endpoint: `/api/AiChat/GetChatHistory`,
            method: 'POST',
            body: filter
        })
    },
    GetSchema: () => {
        return jsonRequest({
            endpoint: `/api/AiChat/GetSchema`,
            method: 'GET'
        })
    },
    GetSchemaWithRules: () => {
        return jsonRequest({
            endpoint: `/api/AiChat/GetSchemaWithRules`,
            method: 'GET'
        })
    },
    SaveSchema: (input) => {
        return jsonRequest({
            endpoint: `/api/AiChat/SaveSchema`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    RefreshCache: () => {
        return jsonRequest({
            endpoint: `/api/AiChat/RefreshCache`,
            method: 'POST',
            showSuccess: true
        })
    }
}

const BulkPriceUpdate = {
    UpdateBaseOperationPrices: (updates) => {
        return jsonRequest({
            endpoint: `/api/BulkPriceUpdate/UpdateBaseOperationPrices`,
            method: 'POST',
            body: { updates },
            showSuccess: true
        })
    },
    UpdatePricesFromCurrent: (request) => {
        return jsonRequest({
            endpoint: `/api/BulkPriceUpdate/UpdatePricesFromCurrent`,
            method: 'POST',
            body: request,
            showSuccess: true
        })
    },
    UpdatePricesFromBase: (request) => {
        return jsonRequest({
            endpoint: `/api/BulkPriceUpdate/UpdatePricesFromBase`,
            method: 'POST',
            body: request,
            showSuccess: true
        })
    },
    UpdatePricesByTags: (request) => {
        return jsonRequest({
            endpoint: `/api/BulkPriceUpdate/UpdatePricesByTags`,
            method: 'POST',
            body: request,
            showSuccess: true
        })
    }
}

const Search = {
    // Mobil canlı arama: tek metinle Patient/Appointment/Operation/Institution sonuçları
    // hideLoader: arama sırasında global request spinner gözükmesin
    // hideError:  AbortError veya hızlı sırayla yazılan istekler için generic toast'u suskuna al
    Live: (text, limit = 5) => {
        return jsonRequest({
            endpoint: `/api/Search/Live`,
            method: 'POST',
            body: { text, limit },
            hideLoader: true,
            hideError: true,
        })
    },
}

const NotificationSettings = {
    GetSettings: () => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/GetSettings`,
            method: 'GET'
        })
    },
    SaveSettings: (data) => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/SaveSettings`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetPlaceholders: () => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/GetPlaceholders`,
            method: 'GET'
        })
    },
    GetSmsBalance: () => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/GetSmsBalance`,
            method: 'GET'
        })
    },
    GetOutboxDataGrid: (gridInput) => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/GetOutboxDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetConsentConfig: (customId) => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/GetConsentConfig${customId ? `?customId=${encodeURIComponent(customId)}` : ''}`,
            method: 'GET'
        })
    },
    GetKvkkDocuments: () => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/GetKvkkDocuments`,
            method: 'GET'
        })
    },
    UploadKvkkDocument: (documentType, file) => {
        return uploadRequest({
            endpoint: `/api/NotificationSettings/UploadKvkkDocument/${documentType}`,
            file,
        })
    },
    SetActiveKvkkDocument: (id) => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/SetActiveKvkkDocument/${id}`,
            method: 'POST',
            showSuccess: true
        })
    },
    SetKvkkDocumentUrl: (id, url) => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/SetKvkkDocumentUrl/${id}`,
            method: 'POST',
            body: { url }
        })
    },
    DownloadKvkkDocument: (id) => {
        return jsonRequest({
            endpoint: `/api/NotificationSettings/DownloadKvkkDocument/${id}`,
            method: 'GET',
            browserDownload: true
        })
    },
}

// XHR ile multipart yükleme + upload progress (fetch progress raporlayamaz).
function uploadWithProgress({ endpoint, formData, onProgress }) {
    return new Promise((resolve) => {
        const config = window.AppSettings;
        let url = `${new URL(window.location.href).origin}${endpoint}`;
        if (config && config.server && config.server.api)
            url = `${config.server.api}${endpoint}`;

        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', getAuthorization());

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress)
                onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            let json = {};
            try { json = JSON.parse(xhr.responseText); } catch { /* ignore */ }
            if (xhr.status === 401) {
                if (document.logout) document.logout();
                resolve({ success: false });
                return;
            }
            if (xhr.status >= 200 && xhr.status < 300)
                resolve({ success: true, ...json });
            else
                resolve({ success: false, message: json.Message || json.message });
        };
        xhr.onerror = () => resolve({ success: false });
        xhr.send(formData);
    });
}

const DicomImport = {
    GetFirmPacs: (patientId) => jsonRequest({ endpoint: `/api/DicomImport/GetFirmPacs/${patientId}`, method: 'GET' }),
    GetStatus: (jobId) => jsonRequest({ endpoint: `/api/DicomImport/GetStatus/${jobId}`, method: 'GET', hideLoader: true }),
    Upload: (formData, onProgress) => uploadWithProgress({ endpoint: `/api/DicomImport/Upload`, formData, onProgress }),
    Confirm: (jobId) => jsonRequest({ endpoint: `/api/DicomImport/Confirm/${jobId}`, method: 'POST' }),
    Cancel: (jobId) => jsonRequest({ endpoint: `/api/DicomImport/Cancel/${jobId}`, method: 'POST' }),
};

const ApiService = {
    setLogout,
    getAuthorization,
    DicomImport,
    Firm,
    Appointment,
    AppointmentTeletip,
    Operation,
    Patient,
    Unit,
    Institution,
    Doctor,
    Reminder,
    User,
    PaymentMethod,
    Payment,
    State,
    Expense,
    CashView,
    Role,
    Dashboard,
    AppLog,
    SmsLog,
    FirmPosDevice,
    CashRegister,
    Auth,
    Notification,
    Entitlement,
    PatientApp,
    Accounting,
    PatientBillingAddress,
    City,
    District,
    Barcode,
    AiChat,
    BulkPriceUpdate,
    EnabizOperation,
    Search,
    NotificationSettings,
}

export default ApiService;
